import { createContext, useContext } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import type { Roles } from './roles'

// ---------------------------------------------------------------------------
// Auth state shape
// ---------------------------------------------------------------------------
//
// `authenticated` carries `roles` because role information lives on the
// **access token**, not the ID token (App Roles emit on the audience they
// are assigned on — that's `sp-tis-p-keystone-api`, the API). The Entra provider
// acquires the access token as part of auth init and decodes its `roles`
// claim into the shape the UI gates read. Subsequent role reads are
// synchronous.
//
// The context lives in its own module so the two providers — Entra and the
// local dev bypass — can both fill it without importing each other, and so
// `useAuthContext` has one definition regardless of which one is mounted.

export type AuthState =
  | { status: 'initialising' }
  /** `reason` is set when a sign-in attempt was refused, so the screen can say why. */
  | { status: 'unauthenticated'; reason?: string }
  | { status: 'authenticated'; account: AccountInfo; roles: Roles }

export interface AuthContextValue {
  authState: AuthState
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  /**
   * Headers that identify the caller to the API. Under Entra that is a bearer
   * token; locally it is the actor headers, because adr/003 defers auth until
   * the funnel is clickable and the API trusts the header on 127.0.0.1.
   *
   * A header map rather than a token string, so the two modes differ in what
   * they send rather than in how every call site is written.
   */
  getAuthHeaders: () => Promise<Record<string, string>>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider')
  return ctx
}
